import type {
  DegradationEvent,
  InterferenceWorkloadCost,
  InterferenceWorkloadKey,
} from '@/lib/urm-api';

export function workloadKey(key: InterferenceWorkloadKey): string {
  return `${key.resource_type}/${key.action}/${key.api_label}`;
}

export function matchesGroupedWorkload(
  group: InterferenceWorkloadKey,
  member: InterferenceWorkloadKey,
): boolean {
  return (group.resource_type === '*' || group.resource_type === member.resource_type)
    && (group.action === '*' || group.action === member.action)
    && (group.api_label === '*' || group.api_label === member.api_label);
}

export function groupedWorkloadMembers(
  cost: InterferenceWorkloadCost,
  workloadKeys: InterferenceWorkloadKey[],
): InterferenceWorkloadKey[] {
  if (!workloadKey(cost.workload).includes('*')) return [];
  const members = new Map<string, InterferenceWorkloadKey>();
  for (const member of workloadKeys) {
    if (matchesGroupedWorkload(cost.workload, member)) members.set(workloadKey(member), member);
  }
  return Array.from(members.values()).sort((a, b) => workloadKey(a).localeCompare(workloadKey(b)));
}

export interface IncidentGroup {
  incidentId: string;
  startMs: number;
  endMs: number;
  events: DegradationEvent[];
}

export function groupEventsByIncident(events: DegradationEvent[]): IncidentGroup[] {
  const groups = new Map<string, IncidentGroup>();
  for (const event of events) {
    const current = groups.get(event.incident_id);
    if (current) {
      current.events.push(event);
      current.startMs = Math.min(current.startMs, event.event_start_ms);
      current.endMs = Math.max(current.endMs, event.event_end_ms);
    } else {
      groups.set(event.incident_id, {
        incidentId: event.incident_id,
        startMs: event.event_start_ms,
        endMs: event.event_end_ms,
        events: [event],
      });
    }
  }
  return Array.from(groups.values()).sort((a, b) => a.startMs - b.startMs);
}
